import React, { useEffect, useRef, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Button, DotIndicator } from '@sololearnorg/sol';
import { AuthService, Container } from '../../../../symphony';

import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlDiscussContext } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';

import s from './ReplyForm.scss';

export function ReplyForm() {
	useStyles(s);
	const maxReplyInputCharsCount = 2048;
	const replyFormRef = useRef<HTMLDivElement>();
	const { actions$ } = Container.take(SlDiscussContext, SlActions);
	const authService = Container.take('global', AuthService);
	const {
		question$: question,
		isSaveReplyLoading$: isSaveReplyLoading,
		editingReplyId$: editingReplyId,
		editingReplyText$: editingReplyText,
	} = useContainerData(SlDiscussContext, SlDataService, [
		'question$',
		'isSaveReplyLoading$',
		'isReplyFormOpen$',
		'editingReplyId$',
		'editingReplyText$',
	]);
	const [replyInputValue, setReplyInputValue] = useState(
		editingReplyText || '',
	);

	const replyInputRemainingCharsCount = maxReplyInputCharsCount - replyInputValue.length;

	const replyChangeHandler = (
		event: React.ChangeEvent<HTMLTextAreaElement>,
	) => {
		setReplyInputValue(
			event.currentTarget.value.substring(0, maxReplyInputCharsCount),
		);
	};

	const saveClickHandler = () => {
		actions$.next({
			type: ActionType.OnReplySaveClick,
			requestType: RequestType.Parallel,
			data: {
				replyContent: replyInputValue,
				id: editingReplyId,
			},
		});
	};

	const cancelClickHandler = () => {
		actions$.next({
			type: ActionType.OnCreateReplyCancelClick,
			requestType: RequestType.Parallel,
		});
	};

	const deleteClickHandler = () => {
		actions$.next({
			type: ActionType.OnDeleteReplyClick,
			requestType: RequestType.Parallel,
			data: {
				id: editingReplyId,
			},
		});
	};

	useEffect(() => {
		replyFormRef.current?.scrollIntoView({
			behavior: 'auto',
			block: 'center',
		});
	}, []);

	return question ? (
		<div className="discuss-reply-form" ref={replyFormRef}>
			{isSaveReplyLoading && <DotIndicator />}
			<div className="discuss-reply-form__user-name">
				{authService.getUser().name}
			</div>
			<textarea
				name="discuss-reply-form__input"
				placeholder="Write your reply here..."
				className="discuss-reply-form__input"
				value={replyInputValue}
				onChange={replyChangeHandler}
			/>
			<div
				className={`discuss-reply-form__remaining-characters-count${replyInputRemainingCharsCount === 0
					? ' discuss-reply-form__remaining-characters-danger'
					: ''
				}`}
			>
				{replyInputRemainingCharsCount}
				{' '}
				character
				{replyInputRemainingCharsCount > 1 ? 's' : ''}
				{' '}
				remaining
			</div>
			<div className="discuss-reply-form__action-buttons">
				{editingReplyId && (
					<Button
						buttonType="secondary"
						size="s"
						onClick={deleteClickHandler}
					>
						Delete
					</Button>
				)}
				<Button
					buttonType="secondary"
					size="s"
					onClick={cancelClickHandler}
				>
					Cancel
				</Button>
				<Button
					buttonType="primary"
					size="s"
					onClick={saveClickHandler}
				>
					{editingReplyId ? 'Save changes' : 'Post'}
				</Button>
			</div>
		</div>
	) : null;
}
